import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Input,
  InputNumber,
  List,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowLeftOutlined,
  ArrowUpOutlined,
  CalculatorOutlined,
  DeleteOutlined,
  PlusOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import { getSelectedProductionTerminology } from "./productionTerminology";
import {
  calculateProductionBatchNeeds,
  getProductionUnits,
  processProductionBatch,
} from "./api";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const MAX_BATCH_ITEMS = 25;
const PRODUCTION_CREATE_PERMISSION =
  "Gestion Commerciale.Approvisionnement.Production.Gerer.create";
const CUISINE_CONVERT_PERMISSION =
  "Gestion Commerciale.Restauration.Cuisine.convert_to_production";

const createClientId = (prefix = "production-batch") => {
  if (
    typeof window !== "undefined" &&
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
  ) {
    return `${prefix}-${window.crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const createEmptyLine = () => ({
  client_line_id: createClientId("line"),
  production_unit_id: null,
  output_quantity: 1,
  restaurant_ticket_id: null,
});

const normalizeInitialLines = (locationState) => {
  const source = Array.isArray(locationState?.initialItems)
    ? locationState.initialItems
    : [];

  if (source.length === 0) {
    return [createEmptyLine()];
  }

  return source.slice(0, MAX_BATCH_ITEMS).map((item, index) => ({
    client_line_id:
      String(item.client_line_id || "").trim() ||
      (item.restaurant_ticket_id
        ? `ticket-${item.restaurant_ticket_id}`
        : createClientId(`line-${index + 1}`)),
    production_unit_id: Number(item.production_unit_id) || null,
    output_quantity:
      Number(item.output_quantity) > 0 ? Number(item.output_quantity) : 1,
    restaurant_ticket_id: Number(item.restaurant_ticket_id) || null,
    source_label:
      item.source_label ||
      item.product_name ||
      item.production_unit_name ||
      null,
  }));
};

const formatMoney = (value) =>
  `${Math.round(Number(value) || 0).toLocaleString("fr-FR")} CFA`;

const formatQuantity = (value, unit) => {
  const number = Number(value) || 0;
  return `${number.toLocaleString("fr-FR", {
    maximumFractionDigits: 4,
  })}${unit ? ` ${unit}` : ""}`;
};

const sumFinancialTracking = (items, field) =>
  (items || []).reduce(
    (sum, item) => sum + (Number(item?.financial_tracking?.[field]) || 0),
    0,
  );

const guardrailAlert = (guardrails) => {
  if (!guardrails || guardrails.status === "ok") {
    return null;
  }

  const reasons =
    guardrails.status === "blocked"
      ? guardrails.blocking_reasons
      : guardrails.warning_reasons;

  return (
    <Alert
      type={guardrails.status === "blocked" ? "error" : "warning"}
      showIcon
      message={
        guardrails.status === "blocked"
          ? "Le lot ne peut pas etre lance"
          : "Verification requise avant lancement"
      }
      description={
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {(reasons || []).map((reason, index) => (
            <li key={`${reason?.code || "reason"}-${index}`}>
              {reason?.message || String(reason)}
            </li>
          ))}
        </ul>
      }
    />
  );
};

const ProductionBatchProcess = ({ restaurantMode = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const { selectedWarehouse, selectedCompany } = useSelection();
  const productionTerminology = getSelectedProductionTerminology(
    user,
    selectedCompany
  );
  const [units, setUnits] = useState([]);
  const [lines, setLines] = useState(() =>
    normalizeInitialLines(location.state),
  );
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [calculation, setCalculation] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [notes, setNotes] = useState("");
  const [executionResult, setExecutionResult] = useState(null);
  const requestIdRef = useRef(createClientId());

  const canExecute = restaurantMode
    ? hasPermission(CUISINE_CONVERT_PERMISSION) ||
      hasPermission(PRODUCTION_CREATE_PERMISSION)
    : hasPermission(PRODUCTION_CREATE_PERMISSION);

  useEffect(() => {
    let active = true;
    const loadUnits = async () => {
      if (!selectedWarehouse || !selectedCompany) {
        setUnits([]);
        return;
      }

      setLoadingUnits(true);
      try {
        const data = await getProductionUnits({
          page: 1,
          limit: 300,
          warehouse_id: selectedWarehouse,
          company_id: selectedCompany,
          status: "active",
        });
        if (active) {
          setUnits(
            (Array.isArray(data?.units) ? data.units : []).filter(
              (unit) =>
                Number(unit.warehouse_id) === Number(selectedWarehouse) &&
                ["active", "actif"].includes(String(unit.status)),
            ),
          );
        }
      } catch (error) {
        if (active) {
          message.error(
            error?.error ||
              `Impossible de charger les ${productionTerminology.unitPlural}.`,
          );
        }
      } finally {
        if (active) {
          setLoadingUnits(false);
        }
      }
    };

    loadUnits();
    return () => {
      active = false;
    };
  }, [
    productionTerminology.unitPlural,
    selectedCompany,
    selectedWarehouse,
  ]);

  const unitOptions = useMemo(
    () =>
      units.map((unit) => ({
        value: Number(unit.id),
        label: unit.name,
      })),
    [units],
  );

  const invalidateCalculation = () => {
    setCalculation(null);
    setAcknowledged(false);
    setExecutionResult(null);
    requestIdRef.current = createClientId();
  };

  const updateLine = (clientLineId, patch) => {
    setLines((current) =>
      current.map((line) =>
        line.client_line_id === clientLineId
          ? { ...line, ...patch }
          : line,
      ),
    );
    invalidateCalculation();
  };

  const removeLine = (clientLineId) => {
    setLines((current) => {
      const next = current.filter(
        (line) => line.client_line_id !== clientLineId,
      );
      return next.length > 0 ? next : [createEmptyLine()];
    });
    invalidateCalculation();
  };

  const moveLine = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= lines.length) {
      return;
    }

    setLines((current) => {
      const next = [...current];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
    invalidateCalculation();
  };

  const addLine = () => {
    if (lines.length >= MAX_BATCH_ITEMS) {
      message.warning(`Un lot est limite a ${MAX_BATCH_ITEMS} productions.`);
      return;
    }
    setLines((current) => [...current, createEmptyLine()]);
    invalidateCalculation();
  };

  const validateLines = () => {
    if (!selectedWarehouse || !selectedCompany) {
      message.error("Selectionnez une entreprise et un établissement.");
      return false;
    }

    const invalid = lines.find(
      (line) =>
        !(Number(line.production_unit_id) > 0) ||
        !(Number(line.output_quantity) > 0),
    );
    if (invalid) {
      message.error(
        "Selectionnez une unite et une quantite positive pour chaque ligne.",
      );
      return false;
    }
    return true;
  };

  const buildItems = (withRecipeVersions = false) =>
    lines.map((line) => {
      const calculatedItem = calculation?.items?.find(
        (item) => item.client_line_id === line.client_line_id,
      );
      return {
        client_line_id: line.client_line_id,
        production_unit_id: Number(line.production_unit_id),
        output_quantity: Number(line.output_quantity),
        restaurant_ticket_id:
          Number(line.restaurant_ticket_id) || undefined,
        ...(withRecipeVersions
          ? {
              recipe_version_hash:
                calculatedItem?.recipe_summary?.version_hash,
              pricing_guard_acknowledged: acknowledged,
            }
          : {}),
      };
    });

  const handleCalculate = async () => {
    if (!validateLines()) {
      return;
    }

    setCalculating(true);
    setExecutionResult(null);
    try {
      const result = await calculateProductionBatchNeeds({
        company_id: selectedCompany,
        warehouse_id: selectedWarehouse,
        items: buildItems(false),
      });
      setCalculation(result);
      setAcknowledged(false);
    } catch (error) {
      message.error(
        error?.error ||
          error?.message ||
          "Le calcul du lot a echoue.",
      );
    } finally {
      setCalculating(false);
    }
  };

  const handleProcess = async () => {
    if (!calculation || calculation.batch_guardrails?.status === "blocked") {
      return;
    }
    if (
      calculation.batch_guardrails?.requires_acknowledgement &&
      !acknowledged
    ) {
      message.warning("Confirmez les avertissements avant le lancement.");
      return;
    }

    setProcessing(true);
    try {
      const result = await processProductionBatch({
        company_id: selectedCompany,
        warehouse_id: selectedWarehouse,
        client_request_id: requestIdRef.current,
        pricing_guard_acknowledged: acknowledged,
        notes,
        items: buildItems(true),
      });
      const normalizedResult = {
        ...result,
        items: (result.items || []).map((item) => ({
          ...item,
          financial_tracking:
            item.financial_tracking ||
            calculation.items?.find(
              (calculatedItem) =>
                calculatedItem.client_line_id === item.client_line_id,
            )?.financial_tracking ||
            null,
        })),
      };
      setExecutionResult(normalizedResult);
      const createdExpenses = sumFinancialTracking(
        normalizedResult.items,
        "expenses_created_count",
      );
      const pendingRegularizations = sumFinancialTracking(
        normalizedResult.items,
        "pending_regularization_count",
      );
      Modal.success({
        title: result.is_duplicate
          ? "Lot deja enregistre"
          : "Lot termine",
        content: (
          <Space direction="vertical">
            <span>
              {normalizedResult.items?.length || 0} production(s) enregistree(s) sans
              execution partielle.
            </span>
            {createdExpenses > 0 ? (
              <span>{createdExpenses} dépense(s) non payée(s) créée(s).</span>
            ) : null}
            {pendingRegularizations > 0 ? (
              <span>
                {pendingRegularizations} charge(s) restent à régulariser depuis
                l'historique.
              </span>
            ) : null}
          </Space>
        ),
      });
    } catch (error) {
      message.error(
        error?.error ||
          error?.message ||
          "Le lot n'a pas ete lance. Aucune execution partielle n'a ete conservee.",
      );
      if (
        [
          "RECIPE_VERSION_MISMATCH",
          "PRODUCTION_STOCK_CHANGED",
          "PRICING_GUARD_BLOCKED",
        ].includes(error?.code)
      ) {
        setCalculation(null);
        setAcknowledged(false);
      }
    } finally {
      setProcessing(false);
    }
  };

  const materialColumns = [
    {
      title: productionTerminology.materialSingularTitle,
      dataIndex: "product_name",
      key: "product_name",
    },
    {
      title: "Disponible",
      key: "available",
      render: (_, material) =>
        formatQuantity(
          material.initial_available_quantity,
          material.unit_short_name,
        ),
    },
    {
      title: "Besoin cumule",
      key: "required",
      render: (_, material) =>
        formatQuantity(
          material.total_required_quantity,
          material.unit_short_name,
        ),
    },
    {
      title: "Etat",
      key: "status",
      render: (_, material) => (
        <Tag color={material.is_sufficient ? "green" : "red"}>
          {material.is_sufficient ? "Disponible" : "Insuffisant"}
        </Tag>
      ),
    },
    {
      title: "Cout estime",
      key: "cost",
      render: (_, material) =>
        formatMoney(material.estimated_total_cost),
    },
  ];

  if (!selectedWarehouse) {
    return (
      <Card>
        <Alert
          type="warning"
          showIcon
          message="Aucun établissement selectionne"
          description="Selectionnez l'établissement dont le stock doit etre utilise."
        />
      </Card>
    );
  }

  return (
    <div style={{ padding: 16, maxWidth: 1280, margin: "0 auto" }}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Retour
        </Button>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            {restaurantMode
              ? "Lancer plusieurs preparations"
              : "Lancer plusieurs productions"}
          </Title>
          <Text type="secondary">
            L'ordre des lignes determine la priorite sur le stock et les lots
            FIFO.
          </Text>
        </div>
      </Space>

      {restaurantMode && lines.every((line) => !line.restaurant_ticket_id) ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Selectionnez d'abord des tickets dans la file Cuisine."
        />
      ) : null}

      <Spin spinning={loadingUnits}>
        <List
          dataSource={lines}
          locale={{ emptyText: <Empty description="Aucune production" /> }}
          renderItem={(line, index) => (
            <List.Item style={{ display: "block", paddingInline: 0 }}>
              <Card
                size="small"
                title={
                  <Space wrap>
                    <Tag>{index + 1}</Tag>
                    <span>
                      {line.source_label ||
                        `Production ${index + 1}`}
                    </span>
                    {line.restaurant_ticket_id ? (
                      <Tag color="blue">
                        Ticket #{line.restaurant_ticket_id}
                      </Tag>
                    ) : null}
                  </Space>
                }
                extra={
                  <Space>
                    <Button
                      size="small"
                      icon={<ArrowUpOutlined />}
                      disabled={index === 0}
                      onClick={() => moveLine(index, -1)}
                      aria-label="Monter cette production"
                    />
                    <Button
                      size="small"
                      icon={<ArrowDownOutlined />}
                      disabled={index === lines.length - 1}
                      onClick={() => moveLine(index, 1)}
                      aria-label="Descendre cette production"
                    />
                    {!restaurantMode ? (
                      <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => removeLine(line.client_line_id)}
                        aria-label="Retirer cette production"
                      />
                    ) : null}
                  </Space>
                }
              >
                <Row gutter={[16, 12]}>
                  <Col xs={24} md={16}>
                    <Text strong>{productionTerminology.unitSingularTitle}</Text>
                    <Select
                      showSearch
                      optionFilterProp="label"
                      style={{ width: "100%", marginTop: 6 }}
                      placeholder={`Sélectionner une ${productionTerminology.unitSingular}`}
                      options={unitOptions}
                      value={line.production_unit_id}
                      disabled={restaurantMode && Boolean(line.restaurant_ticket_id)}
                      onChange={(value) =>
                        updateLine(line.client_line_id, {
                          production_unit_id: value,
                        })
                      }
                    />
                  </Col>
                  <Col xs={24} md={8}>
                    <Text strong>Quantite a produire</Text>
                    <InputNumber
                      min={0.01}
                      max={999999.99}
                      precision={2}
                      style={{ width: "100%", marginTop: 6 }}
                      value={line.output_quantity}
                      onChange={(value) =>
                        updateLine(line.client_line_id, {
                          output_quantity: value,
                        })
                      }
                    />
                  </Col>
                </Row>
              </Card>
            </List.Item>
          )}
        />
      </Spin>

      {!restaurantMode ? (
        <Button
          block
          type="dashed"
          icon={<PlusOutlined />}
          disabled={lines.length >= MAX_BATCH_ITEMS}
          onClick={addLine}
          style={{ marginBottom: 16 }}
        >
          Ajouter une production
        </Button>
      ) : null}

      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text strong>Note commune, facultative</Text>
          <TextArea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            maxLength={1000}
            autoSize={{ minRows: 2, maxRows: 4 }}
          />
          <Button
            type="primary"
            icon={<CalculatorOutlined />}
            loading={calculating}
            onClick={handleCalculate}
          >
            Verifier le stock et les couts
          </Button>
        </Space>
      </Card>

      {calculation ? (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Row gutter={[12, 12]}>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="Productions"
                  value={calculation.batch_summary?.item_count}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="Cout standard"
                  value={formatMoney(
                    calculation.batch_summary?.standard_total_cost,
                  )}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="Cout FIFO estime"
                  value={formatMoney(
                    calculation.batch_summary?.estimated_total_cost,
                  )}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="Ecart"
                  value={formatMoney(
                    calculation.batch_summary?.delta_amount,
                  )}
                />
              </Card>
            </Col>
          </Row>

          {guardrailAlert(calculation.batch_guardrails)}

          {sumFinancialTracking(
            calculation.items,
            "pending_regularization_count",
          ) > 0 ? (
            <Alert
              type="warning"
              showIcon
              message="Lot autorisé avec régularisation financière ultérieure"
              description={`${sumFinancialTracking(
                calculation.items,
                "pending_regularization_count",
              )} charge(s) resteront incluses dans les coûts de production, sans dépense créée, jusqu'à leur régularisation depuis l'historique.`}
            />
          ) : null}

          <Card
            title={productionTerminology.cumulativeMaterialsTitle}
            bodyStyle={{ padding: 0 }}
          >
            <Table
              rowKey="product_id"
              columns={materialColumns}
              dataSource={calculation.materials || []}
              pagination={false}
              scroll={{ x: 760 }}
            />
          </Card>

          <Card title="Revue ligne par ligne">
            <List
              dataSource={calculation.items || []}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <Space wrap>
                        <Tag>{item.sequence}</Tag>
                        <span>{item.unit?.name}</span>
                        <Tag color={item.all_available ? "green" : "red"}>
                          {item.all_available
                            ? "Stock disponible"
                            : "Stock insuffisant"}
                        </Tag>
                        {Number(
                          item.financial_tracking
                            ?.pending_regularization_count,
                        ) > 0 ? (
                          <Tag color="orange">Charge à régulariser</Tag>
                        ) : null}
                      </Space>
                    }
                    description={
                      <Paragraph style={{ margin: 0 }}>
                        Quantite : {formatQuantity(item.output_quantity)} ·
                        Cout estime :{" "}
                        {formatMoney(
                          item.cost_summary?.estimated_total_cost,
                        )}
                      </Paragraph>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>

          {calculation.batch_guardrails?.requires_acknowledgement ? (
            <Checkbox
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
            >
              J'ai verifie les ecarts de cout et je confirme le lancement de
              tout le lot.
            </Checkbox>
          ) : null}

          <Button
            type="primary"
            size="large"
            block
            icon={<RocketOutlined />}
            loading={processing}
            disabled={
              !canExecute ||
              calculation.batch_guardrails?.status === "blocked" ||
              (calculation.batch_guardrails?.requires_acknowledgement &&
                !acknowledged) ||
              Boolean(executionResult)
            }
            onClick={handleProcess}
          >
            {executionResult
              ? "Lot deja enregistre"
              : `Lancer les ${lines.length} productions atomiquement`}
          </Button>

          {!canExecute ? (
            <Alert
              type="warning"
              showIcon
              message="Vous pouvez consulter le calcul mais pas lancer ce lot."
            />
          ) : null}

          {executionResult ? (
            <Alert
              type="success"
              showIcon
              message={`Lot #${executionResult.batch?.id} termine`}
              description={
                <Space direction="vertical">
                  <span>
                    {executionResult.items?.length || 0} production(s)
                    enregistree(s).
                  </span>
                  {sumFinancialTracking(
                    executionResult.items,
                    "expenses_created_count",
                  ) > 0 ? (
                    <span>
                      {sumFinancialTracking(
                        executionResult.items,
                        "expenses_created_count",
                      )} dépense(s) non payée(s) créée(s).
                    </span>
                  ) : null}
                  {sumFinancialTracking(
                    executionResult.items,
                    "pending_regularization_count",
                  ) > 0 ? (
                    <span>
                      {sumFinancialTracking(
                        executionResult.items,
                        "pending_regularization_count",
                      )} charge(s) à régulariser depuis l'historique.
                    </span>
                  ) : null}
                  <Button onClick={() => navigate("/production/history")}>
                    Ouvrir l'historique
                  </Button>
                </Space>
              }
            />
          ) : null}
        </Space>
      ) : null}
    </div>
  );
};

export default ProductionBatchProcess;
